import React from "react";
import TransactionListTriggerButton from "@icoach/accountBook/components/TransactionListTriggerButton";
import { STATISTICS_FILTER_TYPE } from "@icoach/constants";
import { formatCurrencyFn } from "@util/utils";
import { isValidDate, parseDate } from "@util/moment";
import { DateTimeStandard, Typography } from "@curves/ui-base";
import { Link } from "react-router-dom";
import { ACCOUNT_BOOK_ORDERS_ORDER_URL } from "@icoach/router/routerPath";
import { MemoControllerBox } from "@icoach/components";
import { paymentMethodMap } from "@icoach/accountBook/staticData";

/**
 * 取得現場收入分類的表格標題欄位設定。
 * @returns {[{headerLabel: string, cellKey: string, formatCell: (function(*): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},null,null,null]}
 */
export const getAccountBookOrderStatOrderTypeForOnSiteRowLabel = () => {
	return [
		{
			cellKey: "statisticsDate",
			headerLabel: "建立日期",
			formatCell: cellValue => {
				return (
					<React.Fragment>
						<span>{isValidDate(cellValue) ? parseDate(cellValue, DateTimeStandard.DisplayDateBySlash) : cellValue}</span>
					</React.Fragment>
				);
			},
		},
		{
			cellKey: "newSalePrice",
			headerLabel: "新銷售收入",
			align: "right",
			formatCell: (cellValue, { statisticsDate }, { dialogRef }) => {
				return (
					<TransactionListTriggerButton sourceData={{ filterType: STATISTICS_FILTER_TYPE.NEWSALE, statisticsDate, paymentMethod: paymentMethodMap.onSite }} dialogRef={dialogRef}>
						{formatCurrencyFn(cellValue)}
					</TransactionListTriggerButton>
				);
			},
		},
		{
			cellKey: "membershipPrice",
			headerLabel: "會籍收入",
			align: "right",
			formatCell: (cellValue, { statisticsDate }, { dialogRef }) => {
				return (
					<TransactionListTriggerButton sourceData={{ filterType: STATISTICS_FILTER_TYPE.MEMBERSHIP, statisticsDate, paymentMethod: paymentMethodMap.all }} dialogRef={dialogRef}>
						{formatCurrencyFn(cellValue)}
					</TransactionListTriggerButton>
				);
			},
		},
		{
			cellKey: "productSalePrice",
			headerLabel: "商品收入",
			align: "right",
			formatCell: (cellValue, { statisticsDate }, { dialogRef }) => {
				return (
					<TransactionListTriggerButton sourceData={{ filterType: STATISTICS_FILTER_TYPE.PRODUCTSALE, statisticsDate, paymentMethod: paymentMethodMap.onSite }} dialogRef={dialogRef}>
						{formatCurrencyFn(cellValue)}
					</TransactionListTriggerButton>
				);
			},
		},
		{
			cellKey: "healthcareSalePrice",
			headerLabel: "健康產品收入",
			align: "right",
			formatCell: (cellValue, { statisticsDate }, { dialogRef }) => {
				return (
					<TransactionListTriggerButton sourceData={{ filterType: STATISTICS_FILTER_TYPE.HEALTHCARESALE, statisticsDate, paymentMethod: paymentMethodMap.onSite }} dialogRef={dialogRef}>
						{formatCurrencyFn(cellValue)}
					</TransactionListTriggerButton>
				);
			},
		},
		{
			cellKey: "cabinetPrice",
			headerLabel: "租櫃收入",
			align: "right",
			formatCell: (cellValue, { statisticsDate }, { dialogRef }) => {
				return (
					<TransactionListTriggerButton sourceData={{ filterType: STATISTICS_FILTER_TYPE.CABINET, statisticsDate, paymentMethod: paymentMethodMap.onSite }} dialogRef={dialogRef}>
						{formatCurrencyFn(cellValue)}
					</TransactionListTriggerButton>
				);
			},
		},
		{
			cellKey: "deviceOrderSalePrice",
			headerLabel: "智能收入",
			align: "right",
			formatCell: (cellValue, { statisticsDate }, { dialogRef }) => {
				return (
					<TransactionListTriggerButton sourceData={{ filterType: STATISTICS_FILTER_TYPE.DEVICE, statisticsDate, paymentMethod: paymentMethodMap.onSite }} dialogRef={dialogRef}>
						{formatCurrencyFn(cellValue)}
					</TransactionListTriggerButton>
				);
			},
		},
		{
			cellKey: "totalPrice",
			headerLabel: "總計",
			align: "right",
			formatCell: cellValue => formatCurrencyFn(cellValue),
		},
	];
};

/**
 * 取得現場付款來源的表格標題欄位設定。
 * @returns {[{headerLabel: string, cellKey: string, formatCell: (function(*): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},null,null,null]}
 */
export const getAccountBookOrderStatPaymentTypeForOnSiteRowLabel = () => {
	return [
		{
			cellKey: "statisticsDate",
			headerLabel: "建立日期",
			formatCell: (cellValue, rowData) => {
				const _parseDate = (date, format) => {
					return parseDate(date, format, false);
				};
				let _cellValue = cellValue;

				if (isValidDate(cellValue)) _cellValue = _parseDate(cellValue, "YYYY/MM/DD");
				return (
					<React.Fragment>
						<span>{_cellValue}</span>
					</React.Fragment>
				);
			},
		},
		{
			cellKey: "cashTotalPrice",
			headerLabel: "現金",
			align: "right",
			formatCell: cellValue => formatCurrencyFn(cellValue),
		},
		{
			cellKey: "cardTotalPrice",
			headerLabel: "信用卡",
			align: "right",
			formatCell: cellValue => formatCurrencyFn(cellValue),
		},
		{
			cellKey: "totalPrice",
			headerLabel: "總計",
			align: "right",
			formatCell: cellValue => formatCurrencyFn(cellValue),
		},
	];
};

/**
 * 取得總收入分類的表格標題欄位設定。
 * @returns {[{headerLabel: string, cellKey: string, formatCell: (function(*): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},null,null,null]}
 */
export const getAccountBookOrderStatOrderTypeForAllRowLabel = () => {
	return [
		{
			cellKey: "statisticsDate",
			headerLabel: "建立日期",
			formatCell: cellValue => {
				return (
					<React.Fragment>
						<span>{isValidDate(cellValue) ? parseDate(cellValue, DateTimeStandard.DisplayDateBySlash) : cellValue}</span>
					</React.Fragment>
				);
			},
		},
		{
			cellKey: "newSalePrice",
			headerLabel: "新銷售收入",
			align: "right",
			formatCell: (cellValue, { statisticsDate }, { dialogRef }) => {
				return (
					<TransactionListTriggerButton sourceData={{ filterType: STATISTICS_FILTER_TYPE.NEWSALE, statisticsDate, paymentMethod: paymentMethodMap.onSite }} dialogRef={dialogRef}>
						{formatCurrencyFn(cellValue)}
					</TransactionListTriggerButton>
				);
			},
		},
		{
			cellKey: "membershipPrice",
			headerLabel: "會籍收入",
			align: "right",
			formatCell: (cellValue, { statisticsDate }, { dialogRef }) => {
				return (
					<TransactionListTriggerButton sourceData={{ filterType: STATISTICS_FILTER_TYPE.MEMBERSHIP, statisticsDate, paymentMethod: paymentMethodMap.all }} dialogRef={dialogRef}>
						{formatCurrencyFn(cellValue)}
					</TransactionListTriggerButton>
				);
			},
		},
		{
			cellKey: "productSalePrice",
			headerLabel: "商品收入",
			align: "right",
			formatCell: (cellValue, { statisticsDate }, { dialogRef }) => {
				return (
					<TransactionListTriggerButton sourceData={{ filterType: STATISTICS_FILTER_TYPE.PRODUCTSALE, statisticsDate, paymentMethod: paymentMethodMap.onSite }} dialogRef={dialogRef}>
						{formatCurrencyFn(cellValue)}
					</TransactionListTriggerButton>
				);
			},
		},
		{
			cellKey: "healthcareSalePrice",
			headerLabel: "健康產品收入",
			align: "right",
			formatCell: (cellValue, { statisticsDate }, { dialogRef }) => {
				return (
					<TransactionListTriggerButton sourceData={{ filterType: STATISTICS_FILTER_TYPE.HEALTHCARESALE, statisticsDate, paymentMethod: paymentMethodMap.onSite }} dialogRef={dialogRef}>
						{formatCurrencyFn(cellValue)}
					</TransactionListTriggerButton>
				);
			},
		},
		{
			cellKey: "cabinetPrice",
			headerLabel: "租櫃收入",
			align: "right",
			formatCell: (cellValue, { statisticsDate }, { dialogRef }) => {
				return (
					<TransactionListTriggerButton sourceData={{ filterType: STATISTICS_FILTER_TYPE.CABINET, statisticsDate, paymentMethod: paymentMethodMap.onSite }} dialogRef={dialogRef}>
						{formatCurrencyFn(cellValue)}
					</TransactionListTriggerButton>
				);
			},
		},
		{
			cellKey: "deviceOrderSalePrice",
			headerLabel: "智能收入",
			align: "right",
			formatCell: (cellValue, { statisticsDate }, { dialogRef }) => {
				return (
					<TransactionListTriggerButton sourceData={{ filterType: STATISTICS_FILTER_TYPE.DEVICE, statisticsDate, paymentMethod: paymentMethodMap.onSite }} dialogRef={dialogRef}>
						{formatCurrencyFn(cellValue)}
					</TransactionListTriggerButton>
				);
			},
		},
		{
			cellKey: "allOffsitePrice",
			headerLabel: "非現場收入",
			align: "right",
			formatCell: (cellValue, { statisticsDate }, { dialogRef }) => {
				return (
					<TransactionListTriggerButton sourceData={{ filterType: STATISTICS_FILTER_TYPE.OFFSITE, statisticsDate, paymentMethod: paymentMethodMap.all }} dialogRef={dialogRef}>
						{formatCurrencyFn(cellValue)}
					</TransactionListTriggerButton>
				);
			},
		},
		{
			cellKey: "bonusPrice",
			headerLabel: "紅利收入",
			align: "right",
			formatCell: (cellValue, { statisticsDate }, { dialogRef }) => {
				return (
					<TransactionListTriggerButton sourceData={{ filterType: STATISTICS_FILTER_TYPE.BONUS, statisticsDate, paymentMethod: paymentMethodMap.remit }} dialogRef={dialogRef}>
						{formatCurrencyFn(cellValue)}
					</TransactionListTriggerButton>
				);
			},
		},
		{
			cellKey: "deviceOrderBonusPrice",
			headerLabel: "智能紅利收入",
			align: "right",
			formatCell: (cellValue, { statisticsDate }, { dialogRef }) => {
				return (
					<TransactionListTriggerButton sourceData={{ filterType: STATISTICS_FILTER_TYPE.DEVICEOFFSITE, statisticsDate, paymentMethod: paymentMethodMap.remit }} dialogRef={dialogRef}>
						{formatCurrencyFn(cellValue)}
					</TransactionListTriggerButton>
				);
			},
		},
		{
			cellKey: "totalPrice",
			headerLabel: "總收入",
			align: "right",
			formatCell: cellValue => formatCurrencyFn(cellValue),
		},
	];
};

/**
 * 取得總(現場與非現場)付款來源的表格標題欄位設定。
 * @returns {[{headerLabel: string, cellKey: string, formatCell: (function(*): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},null,null,null]}
 */
export const getAccountBookOrderStatPaymentTypeForAllRowLabel = () => {
	return [
		{
			cellKey: "statisticsDate",
			headerLabel: "建立日期",
			formatCell: (cellValue, rowData) => {
				const _parseDate = (date, format) => {
					return parseDate(date, format, false);
				};
				let _cellValue = cellValue;

				if (isValidDate(cellValue)) _cellValue = _parseDate(cellValue, "YYYY/MM/DD");
				return (
					<React.Fragment>
						<span>{_cellValue}</span>
					</React.Fragment>
				);
			},
		},
		{
			cellKey: "cashTotalPrice",
			headerLabel: "現金",
			align: "right",
			formatCell: cellValue => formatCurrencyFn(cellValue),
		},
		{
			cellKey: "allCardTotalPrice",
			headerLabel: "信用卡",
			align: "right",
			formatCell: cellValue => formatCurrencyFn(cellValue),
		},
		{
			cellKey: "achPrice",
			headerLabel: "ACH",
			align: "right",
			formatCell: cellValue => formatCurrencyFn(cellValue),
		},
		{
			cellKey: "inwardRemitPrice",
			headerLabel: "匯入",
			align: "right",
			formatCell: cellValue => formatCurrencyFn(cellValue),
		},
		{
			cellKey: "outwardRemitPrice",
			headerLabel: "匯出",
			align: "right",
			formatCell: cellValue => formatCurrencyFn(cellValue),
		},
		{
			cellKey: "totalPrice",
			headerLabel: "總收入",
			align: "right",
			formatCell: cellValue => formatCurrencyFn(cellValue),
		},
	];
};

/**
 * 1. 取得現場收入基本欄位的標題欄位設定。
 * @returns {[{headerLabel: string, cellKey: string, formatCell: (function(*): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},null,null,null]}
 */
export const getAccountBookOrderBasicForOnSiteRowLabel = () => {
	return [
		{
			cellKey: "orderDate",
			headerLabel: "建立日期",
			formatCell: (cellValue, { orderID, orderNo }) => {
				if (!isValidDate(cellValue)) {
					return cellValue;
				}
				return (
					<React.Fragment>
						<Typography className={"mb-0"}>{parseDate(cellValue, DateTimeStandard.DisplayFullBySlash)}</Typography>
						{orderNo && (
							<Link className={"body2"} to={ACCOUNT_BOOK_ORDERS_ORDER_URL(orderID)}>
								{orderNo}
							</Link>
						)}
					</React.Fragment>
				);
			},
		},
		{
			cellKey: "receiverName",
			headerLabel: "購買人",
			formatCell: cellValue => {
				return <strong>{cellValue}</strong>;
			},
		},
		{
			cellKey: "itemName",
			headerLabel: "品名",
			align: "center",
		},
		{
			cellKey: "invoiceNo",
			headerLabel: "發票號碼",
			align: "center",
		},
		{
			cellKey: "employeeName",
			headerLabel: "擔當",
			align: "center",
		},
	];
};

/**
 * 1-1. 取得現場收入(交易)完整欄位的標題欄位設定。
 * @returns {[{headerLabel: string, cellKey: string, formatCell: (function(*): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},null,null,null]}
 */
export const getAccountBookOrderIncomeForOnSiteRowLabel = () => {
	return [
		...getAccountBookOrderBasicForOnSiteRowLabel(),
		{
			cellKey: "cashPrice",
			headerLabel: "現金付款金額",
			align: "right",
			formatCell: cellValue => {
				return formatCurrencyFn(cellValue);
			},
		},
		{
			cellKey: "cardPrice",
			headerLabel: "信用卡付款金額",
			align: "right",
			formatCell: cellValue => {
				return formatCurrencyFn(cellValue);
			},
		},
		{
			cellKey: "totalPrice",
			headerLabel: "總金額",
			align: "right",
			formatCell: cellValue => {
				return formatCurrencyFn(cellValue);
			},
		},
		{
			cellKey: "note",
			headerLabel: "備註",
			className: "vertical-align-middle",
			formatCell: cellValue => {
				return <MemoControllerBox data={cellValue} dialogTitle={"備註"} />;
			},
		},
	];
};

/**
 * 1-2. 取得現場收入(退費)完整欄位的標題欄位設定。
 * @returns {[{headerLabel: string, cellKey: string, formatCell: (function(*): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},null,null,null]}
 */
export const getAccountBookOrderRefundForOnSiteRowLabel = () => {
	return [
		...getAccountBookOrderBasicForOnSiteRowLabel(),
		{
			cellKey: "returnAmount",
			headerLabel: "退款總金額",
			align: "right",
			formatCell: cellValue => formatCurrencyFn(cellValue),
		},
		{
			cellKey: "note",
			headerLabel: "備註",
			className: "vertical-align-middle",
			formatCell: cellValue => <MemoControllerBox data={cellValue} dialogTitle={"備註"} />,
		},
		{
			cellKey: "returnNotes",
			headerLabel: "退單備註",
			className: "vertical-align-middle",
			formatCell: cellValue => <MemoControllerBox data={cellValue} dialogTitle={"備註"} />,
		},
	];
};

/**
 * 2. 取得匯款基本欄位的標題欄位設定。
 * @returns {[{headerLabel: string, cellKey: string, formatCell: (function(*): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},null,null,null]}
 */
export const getAccountBookOrderBasicForRemitRowLabel = () => {
	return [
		{
			cellKey: "orderDate",
			headerLabel: "建立日期",
			formatCell: (cellValue, { orderID, orderNo }) => {
				if (!isValidDate(cellValue)) {
					return cellValue;
				}
				return (
					<React.Fragment>
						<Typography className={"mb-0"}>{parseDate(cellValue, DateTimeStandard.DisplayFullBySlash)}</Typography>
						{orderNo && (
							<Link className={"body2"} to={ACCOUNT_BOOK_ORDERS_ORDER_URL(orderID)}>
								{orderNo}
							</Link>
						)}
					</React.Fragment>
				);
			},
		},
		{
			cellKey: "receiverName",
			headerLabel: "購買人",
			formatCell: cellValue => {
				return <strong>{cellValue}</strong>;
			},
		},
		{
			cellKey: "itemName",
			headerLabel: "品名",
			align: "center",
		},
		{
			cellKey: "invoiceNo",
			headerLabel: "發票號碼",
			align: "center",
		},
		{
			cellKey: "employeeName",
			headerLabel: "擔當",
			align: "center",
		},
	];
};

/**
 * 2-1. 取得匯款收入(交易)完整欄位的標題欄位設定。
 * @returns {[{headerLabel: string, cellKey: string, formatCell: (function(*): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},null,null,null]}
 */
export const getAccountBookOrderIncomeForRemitRowLabel = () => {
	return [
		...getAccountBookOrderBasicForRemitRowLabel(),
		{
			cellKey: "handingFee",
			headerLabel: "手續費",
			align: "right",
			formatCell: cellValue => {
				return formatCurrencyFn(cellValue);
			},
		},
		{
			cellKey: "totalPrice",
			headerLabel: "匯入金額",
			align: "right",
			formatCell: cellValue => {
				return formatCurrencyFn(cellValue);
			},
		},
		{
			cellKey: "note",
			headerLabel: "備註",
			className: "vertical-align-middle",
			formatCell: cellValue => {
				return <MemoControllerBox data={cellValue} dialogTitle={"備註"} />;
			},
		},
	];
};

/**
 * 2-2. 取得匯款支出完整欄位的標題欄位設定。
 * @returns {[{headerLabel: string, cellKey: string, formatCell: (function(*): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},null,null,null]}
 */
export const getAccountBookOrderExpensesForRemitRowLabel = () => {
	return [
		...getAccountBookOrderBasicForRemitRowLabel(),
		{
			cellKey: "handingFee",
			headerLabel: "手續費",
			align: "right",
			formatCell: cellValue => {
				return formatCurrencyFn(cellValue);
			},
		},
		{
			cellKey: "totalPrice",
			headerLabel: "匯款金額",
			align: "right",
			formatCell: cellValue => {
				return formatCurrencyFn(cellValue);
			},
		},
		{
			cellKey: "note",
			headerLabel: "備註",
			className: "vertical-align-middle",
			formatCell: cellValue => {
				return <MemoControllerBox data={cellValue} dialogTitle={"備註"} />;
			},
		},
		{
			cellKey: "returnNotes",
			headerLabel: "退單備註",
			className: "vertical-align-middle",
			formatCell: cellValue => {
				return <MemoControllerBox data={cellValue} dialogTitle={"備註"} />;
			},
		},
	];
};

/**
 * 2-3. 取得匯款收入(退費)完整欄位的標題欄位設定。
 * @returns {[{headerLabel: string, cellKey: string, formatCell: (function(*): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},{headerLabel: string, cellKey: string, align: string, formatCell: (function(*, {statisticsDate: *}): *)},null,null,null]}
 */
export const getAccountBookOrderRefundForRemitRowLabel = () => {
	return [
		...getAccountBookOrderBasicForRemitRowLabel(),
		{
			cellKey: "returnAmount",
			headerLabel: "退費金額",
			align: "right",
			formatCell: cellValue => {
				return formatCurrencyFn(cellValue);
			},
		},
		{
			cellKey: "totalPrice",
			headerLabel: "匯出金額",
			align: "right",
			formatCell: cellValue => {
				return formatCurrencyFn(cellValue);
			},
		},
		{
			cellKey: "note",
			headerLabel: "備註",
			className: "vertical-align-middle",
			formatCell: cellValue => {
				return <MemoControllerBox data={cellValue} dialogTitle={"備註"} />;
			},
		},
		{
			cellKey: "returnNotes",
			headerLabel: "退單備註",
			className: "vertical-align-middle",
			formatCell: cellValue => {
				return <MemoControllerBox data={cellValue} dialogTitle={"備註"} />;
			},
		},
	];
};

/**
 * 計算指定欄位（keys）在整個資料陣列中的總和。
 *
 * @param {Array<Object>} data - 包含多筆資料的陣列，每筆為物件。
 * @param {Array<Object>} keys - 欲計算總和的欄位 key 值陣列。
 * @returns {Object} 每個 key 對應的加總結果，例如 { price: 100, quantity: 20 }
 */
export const calculateColumnSums = (data, keys) => {
	return data.reduce((acc, row) => {
		keys.forEach(key => {
			const value = parseFloat(row[key]) || 0;
			acc[key] = (acc[key] || 0) + value;
		});
		return acc;
	}, {});
};

/**
 * 將總計資料列加入原始資料中。
 *
 * @param {Array<Object>} rowsData - 原始表格資料，每一列是一個物件。
 * @param {Array<{ cellKey: string }>} headerRow - 用來定義表格欄位與 key 對應關係的描述陣列。
 * @param {Array<string>} initKeys - 需要計算總和的欄位 key 清單。
 * @returns {Array<Object>} - 回傳加上總計列的新資料陣列。
 */
export const appendSummaryRow = (rowsData, headerRow, initKeys) => {
	const totalData = calculateColumnSums(rowsData, initKeys);
	return headerRow.reduce((acc, { cellKey }, currentIndex) => {
		if (currentIndex === 0) {
			acc[cellKey] = "總計";
		} else if (totalData.hasOwnProperty(cellKey)) {
			acc[cellKey] = totalData[cellKey];
		} else {
			acc[cellKey] = "";
		}
		return acc;
	}, {});
};
